import React, { useContext, useState } from 'react';
import { NonOptional } from '../../../index';
import { useSaveDrugs } from './use-save-drugs';
import { DrugSelectionModal } from '../ui/drug-selection-modal';
import { DrugOutput } from '@coverright/data-access/types/medicare';
import { MedicareQuoteQuery } from '@coverright/data-access/medicare';

interface Props {
  handleAddClick: () => void,
  handleEditClick: (drug: DrugOutput) => void,
  handleDeleteClick: (drug: DrugOutput) => void,
  drugs: DrugOutput[],
}

const DrugSelectionContext = React.createContext<Props>({
  handleAddClick: () => {},
  handleEditClick: () => {},
  handleDeleteClick: () => {},
  drugs: []
})

export function withDrugSelection<P>(WrappedComponent: React.FC<P & {quote: NonOptional<MedicareQuoteQuery['medicareQuote']>}>) {
  return (props: P & {quote: NonOptional<MedicareQuoteQuery['medicareQuote']>}) => {
    const {preferredDrugs, zip} = props.quote;
    const [editDrug, setEditDrug] = useState<DrugOutput>();
    const saveDrugs = useSaveDrugs(props.quote);
    const [modalOpen, setModalOpen] = useState(false);

    const handleAddClick = () => {
      setModalOpen(true)
    };

    const handleDeleteClick = (drug: DrugOutput) => {
      saveDrugs(preferredDrugs.filter(
        (v) => v.rxcui !== drug.rxcui || v.packRxcui !== drug.packRxcui
      ))
    };

    const handleEditClick = (drug: DrugOutput) => {
      setEditDrug(drug);
      setModalOpen(true);
    }

    const handleDrugChange  = (drug?: DrugOutput) => {
      if (drug) {
        const result = [...preferredDrugs];
        // edit mode
        if (editDrug !== undefined) {
          const index = preferredDrugs.indexOf(editDrug);
          result[index] = drug;
        } else {
          result.push(drug)
        }
        saveDrugs(result);
      }
      setEditDrug(undefined)
      setModalOpen(false)
    };

    return <DrugSelectionContext.Provider value={{
      drugs: preferredDrugs,
      handleDeleteClick,
      handleAddClick,
      handleEditClick
    }}>
      <DrugSelectionModal open={modalOpen} onClose={handleDrugChange} drug={editDrug} zip={zip} />

      <WrappedComponent {...props} />
    </DrugSelectionContext.Provider>
  }
}

export const useDrugSelection = () => useContext(DrugSelectionContext);
